import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  LayoutDashboard, 
  Plus, 
  BarChart3, 
  Map, 
  FileText, 
  LogOut,
  Users,
  TrendingUp,
  Calendar,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  ChevronRight,
  MessageSquare,
  Layout
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, surveyId: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [surveysRes, overviewRes] = await Promise.all([
        fetch(`${API}/surveys`, { credentials: 'include' }),
        fetch(`${API}/analytics/overview`, { credentials: 'include' })
      ]);

      if (surveysRes.ok) {
        const surveysData = await surveysRes.json();
        setSurveys(surveysData);
      }

      if (overviewRes.ok) {
        const overviewData = await overviewRes.json();
        setOverview(overviewData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDeleteSurvey = async () => {
    if (!deleteDialog.surveyId) return;

    try {
      const response = await fetch(`${API}/surveys/${deleteDialog.surveyId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Survey deleted successfully');
        setSurveys(surveys.filter(s => s.survey_id !== deleteDialog.surveyId));
      } else {
        toast.error('Failed to delete survey');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete survey');
    } finally {
      setDeleteDialog({ open: false, surveyId: null });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSurveyStatus = (survey) => {
    const now = new Date();
    const start = new Date(survey.start_date);
    const end = new Date(survey.end_date);

    if (!survey.is_active) return { label: 'Inactive', variant: 'secondary' };
    if (now < start) return { label: 'Scheduled', variant: 'outline' };
    if (now > end) return { label: 'Ended', variant: 'secondary' };
    return { label: 'Active', variant: 'default' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-foreground text-background min-h-screen fixed left-0 top-0">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center">
              <span className="text-foreground font-bold text-sm">CV</span>
            </div>
            <span className="font-bold text-lg">Admin</span>
          </div>

          <nav className="space-y-2">
            <Link to="/admin" className="sidebar-nav-item active flex items-center gap-3 px-4 py-3 rounded-lg">
              <LayoutDashboard className="h-5 w-5" />
              <span>Dashboard</span>
            </Link>
            <Link to="/admin/templates" className="sidebar-nav-item flex items-center gap-3 px-4 py-3 rounded-lg">
              <Layout className="h-5 w-5" />
              <span>Templates</span>
            </Link>
            <Link to="/admin/surveys/new" className="sidebar-nav-item flex items-center gap-3 px-4 py-3 rounded-lg">
              <Plus className="h-5 w-5" />
              <span>Custom Survey</span>
            </Link>
            <Link to="/admin/feedback" className="sidebar-nav-item flex items-center gap-3 px-4 py-3 rounded-lg">
              <MessageSquare className="h-5 w-5" />
              <span>Feedback Routing</span>
            </Link>
            <Link to="/analytics" className="sidebar-nav-item flex items-center gap-3 px-4 py-3 rounded-lg">
              <BarChart3 className="h-5 w-5" />
              <span>Analytics</span>
            </Link>
            <Link to="/map" className="sidebar-nav-item flex items-center gap-3 px-4 py-3 rounded-lg">
              <Map className="h-5 w-5" />
              <span>Map View</span>
            </Link>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-background/10">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                <span className="text-foreground font-semibold">{user?.name?.[0] || 'A'}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-background/60 truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-background/70 hover:text-background hover:bg-background/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">Manage surveys and view insights</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/templates">
              <Button variant="outline" data-testid="use-template-btn">
                <Layout className="mr-2 h-4 w-4" />
                Use Template
              </Button>
            </Link>
            <Link to="/admin/surveys/new">
              <Button data-testid="create-survey-btn">
                <Plus className="mr-2 h-4 w-4" />
                Custom Survey
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Surveys</p>
                  <p className="text-3xl font-bold">{overview?.total_surveys || 0}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Surveys</p>
                  <p className="text-3xl font-bold">{overview?.active_surveys || 0}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Responses</p>
                  <p className="text-3xl font-bold">{(overview?.total_responses || 0).toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="metric-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">States Active</p>
                  <p className="text-3xl font-bold">{overview?.top_states?.length || 0}</p>
                </div>
                <div className="w-12 h-12 bg-secondary/20 rounded-lg flex items-center justify-center">
                  <Map className="h-6 w-6 text-secondary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Surveys Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Surveys</CardTitle>
            <CardDescription>Manage your governance surveys</CardDescription>
          </CardHeader>
          <CardContent>
            {surveys.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No surveys yet</h3>
                <p className="text-muted-foreground mb-4">Create your first survey to get started</p>
                <Link to="/admin/surveys/new">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Survey
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {surveys.map((survey) => {
                  const status = getSurveyStatus(survey);
                  return (
                    <div 
                      key={survey.survey_id} 
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                      data-testid={`survey-row-${survey.survey_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-semibold truncate">{survey.title}</h4>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(survey.start_date)} - {formatDate(survey.end_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {survey.response_count || 0} responses
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {survey.policy_category}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Link to={`/admin/surveys/${survey.survey_id}/analytics`}>
                          <Button variant="ghost" size="sm">
                            <BarChart3 className="h-4 w-4" />
                          </Button>
                        </Link>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/survey/${survey.survey_id}`} className="flex items-center">
                                <Eye className="mr-2 h-4 w-4" />
                                View Survey
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/admin/surveys/${survey.survey_id}/edit`} className="flex items-center">
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/admin/reports/${survey.survey_id}`} className="flex items-center">
                                <FileText className="mr-2 h-4 w-4" />
                                Generate Report
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => setDeleteDialog({ open: true, surveyId: survey.survey_id })}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        {overview?.top_states?.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Top Responding States</CardTitle>
              <CardDescription>States with the most survey participation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {overview.top_states.slice(0, 5).map((state, index) => (
                  <div key={state._id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                        {index + 1}
                      </span>
                      <span className="font-medium">{state._id}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{state.count} responses</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, surveyId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Survey?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the survey and all its responses.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSurvey} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
